import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Localization, SessionContext, AppMessagesContext, actionSetErrorMessage } from "@viamap/viamap2-common";
import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { ManagedWindow, closeWindow, WindowId, restoreWindowsIfMinimized } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { MapitStateContext, actionRemoveDataLayer } from "src/states/MapitState";
import { LayerType, type LayerInfo, LayerVariabilityType } from "src/common/managers/Types";
import { AnnotationType, AnnotationContextType, type ANNLayerSnapshot, ANNOTATION_TYPES, type AnnotationTypeCategory, AnnotationQuality } from "./ANNTypes";
import { snapshotLayer } from "./ANNFunc";
import { useSaveAnnotation } from "./ANNState";
import {
   ANNEditorActionType,
   useANNEditorContext,
} from "./ANNEditorState";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
interface ANNEditorProps {
   managedWindow: ManagedWindow;
}
export function ANNEditor({ managedWindow }: ANNEditorProps) {
   const { dispatch: windowDispatch, state: windowState } = useWindowContext();
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { state: sessionState } = useContext(SessionContext);
   const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const { state: editorState, dispatch: editorDispatch } = useANNEditorContext();
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const saveAnnotation = useSaveAnnotation();
   const canVerify = hasAccessToFeature(Feature.AnnotationAuthUser);
   const windowParams = managedWindow.windowParams as {
      contextType?: AnnotationContextType;
      identifier?: string | number;
      datoloebenr?: string;
      originalCreatedAt?: string;
      originalCreatedBy?: string;
      originalCreatedByName?: string;
      restoreMinimizedWindowIds?: WindowId[];
   } | undefined;
   const datoloebenrFromParams = windowParams?.datoloebenr;
   const [selectedLayerId, setSelectedLayerId] = useState<number | "">("");
   const [verifyStamp, setVerifyStamp] = useState(false);
   const organizationId = editorState.editSession?.organizationId || sessionState?.customerRef;
   const { contextType, identifier, pendingLayers, stedfaestelseTitle, stedfaestelseHistoryComment, stedfaestelseAnnotationType, stedfaestelseQuality, editSession, saving, error } = editorState;
   useEffect(() => {
      if (!editorState.isOpen) return;
      const valid = pendingLayers.filter((layerId) => {
         const layer = mapitState.layers?.[layerId];
         return !!(layer && snapshotLayer(layer));
      });
      if (valid.length !== pendingLayers.length) {
         editorDispatch({ type: ANNEditorActionType.SetLayers, payload: valid });
      }
   }, [editorState.isOpen, pendingLayers, mapitState.layers, editorDispatch]);
   const availableLayers = useMemo(() => {
      return Object.values(mapitState.layers || {})
         .filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer)
         .map(layer => {
            const snapshot = snapshotLayer(layer);
            if (!snapshot) return null;
            const typeLabel = layer.type === LayerType.ImageOverlay ? "Image" :
               layer.type === LayerType.GeoJSON_Line ? "Line" :
                  layer.type === LayerType.GeoJSON_Polygon ? "Polygon" :
                     layer.type === LayerType.GeoJSON_Point ? "Point" : "Other";
            return { layer, snapshot, typeLabel };
         })
         .filter((item): item is { layer: LayerInfo; snapshot: ANNLayerSnapshot; typeLabel: string } => item !== null);
   }, [mapitState.layers]);
   const availableToAdd = useMemo(() => {
      const pendingLayerIds = new Set<number>();
      pendingLayers.forEach((id) => pendingLayerIds.add(id));

      return availableLayers.filter(item => {
         const layerId = item.layer.layerId;
         return typeof layerId === "number" ? !pendingLayerIds.has(layerId) : true;
      });
   }, [availableLayers, pendingLayers]);
   const handleAddLayer = useCallback(() => {
      if (selectedLayerId === "" || selectedLayerId === null || selectedLayerId === undefined) return;
      const layerId = typeof selectedLayerId === "number" ? selectedLayerId : parseInt(String(selectedLayerId));
      if (isNaN(layerId)) return;
      const item = availableLayers.find(a => a.layer.layerId === layerId);
      if (!item) return;
      editorDispatch({
         type: ANNEditorActionType.AddLayer,
         payload: layerId,
      });
      setSelectedLayerId("");
   }, [selectedLayerId, availableLayers, editorDispatch]);
   const handleRemoveLayer = useCallback((layerId: number) => {
      editorDispatch({ type: ANNEditorActionType.RemoveLayer, payload: layerId });
   }, [editorDispatch]);
   const handleClose = useCallback(() => {
      const initialLayerIds = editSession?.initialLayerIds ? new Set(editSession.initialLayerIds) : undefined;
      if (initialLayerIds && initialLayerIds.size > 0) {
         setTimeout(() => {
            const currentLayerIds = Object.keys(mapitState.layers || {}).map(id => parseInt(id));
            const addedLayerIds = currentLayerIds.filter(id => !initialLayerIds.has(id));
            addedLayerIds.forEach(layerId => {
               const layer = mapitState.layers[layerId];
               if (layer && layer.handle) {
                  try {
                     mapitStateDispatch(actionRemoveDataLayer(layerId));
                  } catch {
                  }
               }
            });
         }, 200);
      }
      editorDispatch({ type: ANNEditorActionType.ClearEditSession });
      editorDispatch({ type: ANNEditorActionType.Close });
      windowDispatch(closeWindow(WindowId.ANNEditor));
      const restoreIds = windowParams?.restoreMinimizedWindowIds?.length
         ? windowParams.restoreMinimizedWindowIds
         : [WindowId.PropertyInformationDialog, WindowId.ServitutDetails, WindowId.ANNUnverifiedAnnotations];
      restoreWindowsIfMinimized(windowState, restoreIds, windowDispatch);
   }, [editorDispatch, windowDispatch, windowState, windowParams, mapitState, mapitStateDispatch, editSession]);
   const totalItems = pendingLayers.length;
   const hasStedfaestelse = pendingLayers.length > 0;
   const handleSaveAll = useCallback(async () => {
      if (!contextType || identifier === undefined || identifier === null || identifier === "") {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No identifier")));
         return;
      }
      if (!organizationId) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No organization selected")));
         return;
      }
      if (totalItems === 0) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Nothing to save")));
         return;
      }
      if (!stedfaestelseTitle.trim()) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Title is required")));
         return;
      }
      editorDispatch({ type: ANNEditorActionType.SaveStart });
      try {

         const oldAnnotationId = editSession?.oldAnnotationId;
         const editDatoloebenr = editSession?.datoloebenr;
         const editOriginalCreatedAt = editSession?.originalCreatedAt;
         const editOriginalCreatedBy = editSession?.originalCreatedBy;
         const editOriginalCreatedByName = editSession?.originalCreatedByName;
         const datoloebenrToSave = editDatoloebenr || datoloebenrFromParams;

         if (pendingLayers.length > 0) {
            const layerSnapshots = pendingLayers
               .map((layerId) => {
                  const liveLayer = mapitState.layers?.[layerId];
                  return liveLayer ? snapshotLayer(liveLayer) : undefined;
               })
               .filter((s): s is ANNLayerSnapshot => !!s);

            if (layerSnapshots.length === 0) {
               appMessageDispatch(actionSetErrorMessage(Localization.getText("Please select at least one layer")));
               editorDispatch({ type: ANNEditorActionType.SaveFailure, payload: "No valid layers to save" });
               return;
            }
            await saveAnnotation({
               contextType,
               documentId: contextType === AnnotationContextType.Servitut ? String(identifier) : undefined,
               bfeNumber: contextType === AnnotationContextType.Ejendom ? identifier : undefined,
               datoloebenr: datoloebenrToSave,
               organizationId,
               type: AnnotationType.Stedfaestelse,
               title: stedfaestelseTitle.trim(),
               historyComment: stedfaestelseHistoryComment.trim() || undefined,
               annotationType: stedfaestelseAnnotationType,
               quality: stedfaestelseQuality,
               verified: canVerify && verifyStamp,
               userId: sessionState?.userRef || "anonymous",
               userName: sessionState?.userName || sessionState?.userRef || "anonymous",
               layers: layerSnapshots,
               id: oldAnnotationId,
               originalCreatedAt: editOriginalCreatedAt || windowParams?.originalCreatedAt,
               originalCreatedBy: editOriginalCreatedBy || windowParams?.originalCreatedBy,
               originalCreatedByName: editOriginalCreatedByName || windowParams?.originalCreatedByName,
            });
         }

         editorDispatch({ type: ANNEditorActionType.SaveSuccess });
         handleClose();
      } catch (err: any) {
         editorDispatch({ type: ANNEditorActionType.SaveFailure, payload: err?.message || String(err) });
         appMessageDispatch(actionSetErrorMessage(err?.message || "Failed to save annotations"));
      }
   }, [
      contextType, identifier, organizationId, totalItems, pendingLayers, editSession,
      stedfaestelseTitle, stedfaestelseHistoryComment, stedfaestelseAnnotationType, stedfaestelseQuality, verifyStamp, canVerify, sessionState, editorDispatch, appMessageDispatch, handleClose, datoloebenrFromParams, windowParams, mapitState, mapitStateDispatch, saveAnnotation
   ]);
   const saveDisabled = useMemo(() => {
      return saving
         || totalItems === 0
         || !stedfaestelseTitle.trim()
         || !organizationId
         || !contextType
         || identifier === undefined || identifier === null || identifier === "";
   }, [saving, totalItems, stedfaestelseTitle, organizationId, contextType, identifier]);
   const contextLabel = contextType === AnnotationContextType.Servitut
      ? `${Localization.getText("Document")}: ${identifier}`
      : `BFE: ${identifier}`;
   return (
      <AdvancedDragModal
         window={managedWindow}
         title={Localization.getText("Stedfaestelse")}
         allowFullscreen={true}
         allowUserMiniMize={true}
         PosDefault={{ left: "calc(100% - 440px)", top: "80px", width: "420px", height: "85vh" }}
         onClose={handleClose}
      >
         <ADModalBody>
            <div className="ann-editor-container">
               <div className="ann-editor-header">
                  {contextLabel}
               </div>
               <div className="ann-editor-content">
                  {error && (
                     <div className="ann-editor-error">
                        {error}
                     </div>
                  )}
                  <div className="ann-editor-field-group">
                     <label className="ann-editor-label">
                        {Localization.getText("Annotation Type")}
                     </label>
                     <select
                        value={stedfaestelseAnnotationType || "Stedfæstelse"}
                        onChange={(e) => editorDispatch({
                           type: ANNEditorActionType.SetStedfaestelseAnnotationType,
                           payload: e.target.value as AnnotationTypeCategory
                        })}
                        className="ann-editor-select"
                     >
                        {ANNOTATION_TYPES.map(type => (
                           <option key={type} value={type}>{Localization.getText(type)}</option>
                        ))}
                     </select>
                  </div>
                  <div className="ann-editor-field-group">
                     <label className="ann-editor-label">
                        {Localization.getText("Quality Assessment")}
                     </label>
                     <div className="ann-editor-quality-selector-group">
                        <div
                           onClick={() => editorDispatch({
                              type: ANNEditorActionType.SetStedfaestelseQuality,
                              payload: undefined
                           })}
                           className={`ann-editor-quality-selector ${stedfaestelseQuality === undefined ? "ann-editor-quality-selector-none ann-editor-quality-selector-selected" : ""}`}
                        >
                           <span className="ann-editor-quality-label">{Localization.getText("None")}</span>
                        </div>
                        {[AnnotationQuality.Gold, AnnotationQuality.Silver, AnnotationQuality.Bronze].map((q) => {
                           const isSelected = stedfaestelseQuality === q;
                           const label = q === AnnotationQuality.Gold ? Localization.getText("Gold") : q === AnnotationQuality.Silver ? Localization.getText("Silver") : Localization.getText("Bronze");
                           return (
                              <div
                                 key={q}
                                 onClick={() => editorDispatch({
                                    type: ANNEditorActionType.SetStedfaestelseQuality,
                                    payload: q
                                 })}
                                 className={`ann-editor-quality-selector ${isSelected ? "ann-editor-quality-selector-selected" : ""} ann-quality-${q.toLowerCase()}`}
                              >
                                 <div className={`ann-editor-quality-indicator-circle ${isSelected ? "ann-editor-quality-indicator-circle-selected" : ""}`} />
                                 <span className={`ann-editor-quality-label ${isSelected ? "ann-editor-quality-label-selected" : ""}`}>{label}</span>
                              </div>
                           );
                        })}
                     </div>
                  </div>
                  <div className="ann-editor-field-group">
                     <label className="ann-editor-label">
                        {Localization.getText("Title")}
                     </label>
                     <input
                        value={stedfaestelseTitle}
                        onChange={e => editorDispatch({ type: ANNEditorActionType.SetStedfaestelseTitle, payload: e.target.value })}
                        className="ann-editor-input"
                     />
                  </div>
                  <div className="ann-editor-field-group">
                     <label className="ann-editor-label">
                        {Localization.getText("History comment")}
                     </label>
                     <textarea
                        rows={3}
                        value={stedfaestelseHistoryComment}
                        onChange={e => editorDispatch({ type: ANNEditorActionType.SetStedfaestelseHistoryComment, payload: e.target.value })}
                        placeholder={Localization.getText("Optional history comment")}
                        className="ann-editor-textarea"
                     />
                  </div>
                  <div className="ann-editor-field-group">
                     <label className="ann-editor-label">
                        {Localization.getText("Layers")}
                     </label>
                     {availableToAdd.length > 0 ? (
                        <div className="ann-editor-layerAddRow">
                           <select
                              value={selectedLayerId === "" ? "" : String(selectedLayerId)}
                              onChange={(e) => setSelectedLayerId(e.target.value === "" ? "" : parseInt(e.target.value))}
                              className="ann-editor-select"
                           >
                              <option value="">{Localization.getText("Select layer...")}</option>
                              {availableToAdd.map(item => (
                                 <option key={item.layer.layerId} value={item.layer.layerId}>
                                    {item.layer.datasetname || item.layer.filename || `Layer ${item.layer.layerId}`} ({item.typeLabel})
                                 </option>
                              ))}
                           </select>
                           <button
                              onClick={handleAddLayer}
                              disabled={selectedLayerId === "" || selectedLayerId === null || selectedLayerId === undefined}
                              className={`ann-editor-button ann-editor-button-primary ann-editor-layerAddButton ${(selectedLayerId === "" || selectedLayerId === null || selectedLayerId === undefined) ? "ann-editor-button-disabled" : ""}`}
                              style={{ fontSize: "18px" }}
                           >
                              <MIBIcon icon="add" />
                              {Localization.getText("Add")}
                           </button>
                        </div>
                     ) : (
                        <div className="ann-editor-empty-state">
                           {Localization.getText("No layers available")}
                        </div>
                     )}
                  </div>
                  {pendingLayers.length > 0 && (
                     <div className="ann-editor-added-items-section">
                        <div className="ann-editor-added-items-list">
                           {pendingLayers.map(layerId => {
                              const layer = mapitState.layers?.[layerId];
                              const name = layer?.datasetname || layer?.filename || `Layer ${layerId}`;
                              const typeLabel = layer?.type === LayerType.ImageOverlay ? "Image" :
                                 layer?.type === LayerType.GeoJSON_Line ? "Line" :
                                    layer?.type === LayerType.GeoJSON_Polygon ? "Polygon" :
                                       layer?.type === LayerType.GeoJSON_Point ? "Point" : "Other";
                              return (
                                 <div key={layerId} className="ann-editor-added-item-row">
                                    <div className="ann-editor-added-item-content">
                                       <span className="ann-editor-added-item-icon">
                                          <MIBIcon icon="layer" />
                                       </span>
                                       <span className="ann-editor-added-item-text">
                                          {name}
                                       </span>
                                       <span className="ann-editor-added-item-badge">
                                          {typeLabel}
                                       </span>
                                    </div>
                                    <button
                                       type="button"
                                       onClick={() => handleRemoveLayer(layerId)}
                                       className="ann-editor-remove-button"
                                       title={Localization.getText("Remove")}
                                       style={{ fontSize: "18px" }}
                                    >
                                       <MIBIcon icon="remove" />
                                    </button>
                                 </div>
                              );
                           })}
                        </div>
                     </div>
                  )}
               </div>
               <div className="ann-editor-footer">
                  <div className="ann-editor-summary">
                     <strong>{Localization.getText("Summary")}:</strong>{" "}
                     {hasStedfaestelse && <span>1 {Localization.getText("Stedfaestelse")} ({pendingLayers.length} {Localization.getText("layers")})</span>}
                     {totalItems === 0 && <span>{Localization.getText("Nothing to save")}</span>}
                  </div>
                  {canVerify && (
                     <div className="ann-editor-verifyBox">
                        <label
                           className={`ann-editor-verifyCheckboxLabel ${verifyStamp ? "ann-editor-verifyCheckboxLabel--checked" : ""}`}
                        >
                           <input
                              type="checkbox"
                              checked={verifyStamp}
                              onChange={(e) => setVerifyStamp(e.target.checked)}
                              className="ann-editor-verifyCheckbox"
                           />
                           <span
                              style={{ fontSize: "18px" }}
                              className="ann-editor-verifyIcon"

                           >
                              <MIBIcon icon="userVerified" />
                           </span>
                           <span className="ann-editor-verifyText">
                              {Localization.getText("Stamp this annotation as verified")}
                           </span>
                        </label>
                     </div>
                  )}
                  <div className="ann-editor-button-group">
                     <button
                        onClick={handleSaveAll}
                        disabled={saveDisabled}
                        className={`ann-editor-button ann-editor-button-primary ann-editor-saveButton ${saveDisabled ? "ann-editor-button-disabled" : ""}`}
                     >
                        {saving ? Localization.getText("Saving...") : Localization.getText("Save All")}
                     </button>
                     <button
                        onClick={handleClose}
                        disabled={saving}
                        className={`ann-editor-button ann-editor-button-secondary ${saving ? "ann-editor-button-disabled" : ""}`}
                     >
                        {Localization.getText("Cancel")}
                     </button>
                  </div>
               </div>
            </div>
         </ADModalBody>
      </AdvancedDragModal>
   );
}
